import { useRef, useState } from 'react';
import { useFrame, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import type { Airport } from '@/types';
import { latLonToVector3 } from '@/utils';

interface AirportMarkerProps {
  airport: Airport;
  color: string;
  hasVideo?: boolean;
  onClick?: () => void;
}

export function AirportMarker({ airport, color, hasVideo = false, onClick }: AirportMarkerProps) {
  const ringRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);
  const position = latLonToVector3(airport.lat, airport.lon, 1.01);
  
  // Use a special color for airports with videos
  const markerColor = hasVideo ? '#ff6b9d' : color;

  // Animate pulse effect
  useFrame(({ clock }) => {
    if (!ringRef.current) return;
    const scale = 1 + 0.3 * Math.sin(clock.elapsedTime * 3);
    ringRef.current.scale.set(scale, scale, scale);
  });

  const colorValue = new THREE.Color(markerColor);

  const handleClick = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    onClick?.();
  };

  const handlePointerOver = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    setHovered(true);
    document.body.style.cursor = 'pointer';
  };

  const handlePointerOut = () => {
    setHovered(false);
    document.body.style.cursor = 'auto';
  };

  return (
    <group position={position}>
      {/* Main marker dot */}
      <mesh
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      >
        <sphereGeometry args={[hovered ? 0.016 : 0.012, 16, 16]} />
        <meshBasicMaterial 
          color={colorValue} 
          transparent 
          opacity={hovered ? 1.0 : 0.9} 
        />
      </mesh>

      {/* Pulsing ring */}
      <mesh
        ref={ringRef}
        rotation={[
          Math.atan2(position[1], Math.sqrt(position[0] ** 2 + position[2] ** 2)),
          Math.atan2(position[0], position[2]),
          0,
        ]}
      >
        <ringGeometry args={[0.015, 0.025, 32]} />
        <meshBasicMaterial
          color={colorValue}
          transparent
          opacity={hovered ? 0.8 : 0.6}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
}
